/**
 * The `/winners` feed sort model — the single source of truth for the winners
 * feed's sort pills. Mirrors `markets-feed-sorts.ts`; the backend (`GET
 * /v1/winners`) sorts winner outcome fields globally
 * by these keys with stable keyset pagination.
 *
 * Plain (non-`'use client'`) module so the `/winners` SSR prefetch and the
 * client `WinnersPage` can both import it.
 */

/**
 * The supported winners-feed sort keys, in pill order. Twenty-four-hour volume
 * is available alongside total volume; newest and holder count remain independently supported.
 */
export const WINNERS_FEED_SORT_KEYS = [
  'createdAt',
  'volumeTotal',
  'volume24h',
  'holderCount',
] as const;

/** A real `/winners` feed sort key (matches the backend `sortBy` enum). */
export type WinnersFeedSortKey = (typeof WINNERS_FEED_SORT_KEYS)[number];

/** The default winners sort — newest first. */
export const WINNERS_FEED_DEFAULT_SORT: WinnersFeedSortKey = 'createdAt';

/** Type guard: is `value` a valid winners sort key? */
export function isWinnersFeedSortKey(value: string): value is WinnersFeedSortKey {
  return (WINNERS_FEED_SORT_KEYS as readonly string[]).includes(value);
}

/** Resolve a URL value to the sort the client and SSR must share. */
export function resolveWinnersFeedSort(value: string | undefined): WinnersFeedSortKey {
  return value !== undefined && isWinnersFeedSortKey(value) ? value : WINNERS_FEED_DEFAULT_SORT;
}

/**
 * Every winners sort descends. Kept as a helper so the client feed and the SSR
 * prefetch produce the same query key.
 */
export function getWinnersFeedSortOrder(_sortBy: WinnersFeedSortKey): 'asc' | 'desc' {
  return 'desc';
}
